import React from 'react';
import { Box, Typography, Chip, Alert, Button, Paper, Divider } from '@mui/material';
import { Warning, EventSeat } from '@mui/icons-material';

const ADMIN_DESKS = new Set([29, 30, 31, 32, 33, 34]);
const DESK_W = 70;
const DESK_H = 48;

// Pairs T1 & T2 are separate 2-desk pods; T3-T5 and all bottom clusters are 4-desk pods.
// Numbers match physical desk labels on the actual office floor plan.
const DESK_POSITIONS = [
  // === TOP ROW ===
  // Pair T1 — desks 1 & 2 (standalone pair, far left)
  { num: 1, x: 29,  y: 22 },
  { num: 2, x: 29,  y: 84 },
  // Pair T2 — desks 3 & 4 (standalone pair)
  { num: 3, x: 133, y: 22 },
  { num: 4, x: 133, y: 84 },
  // Pod T3 — desks 5–8
  { num: 5, x: 247, y: 22 }, { num: 7, x: 327, y: 22 },
  { num: 6, x: 247, y: 84 }, { num: 8, x: 327, y: 84 },
  // Pod T4 — desks 9–12
  { num: 9,  x: 436, y: 22 }, { num: 11, x: 516, y: 22 },
  { num: 10, x: 436, y: 84 }, { num: 12, x: 516, y: 84 },
  // Pod T5 — desks 13–16
  { num: 13, x: 625, y: 22 }, { num: 15, x: 705, y: 22 },
  { num: 14, x: 625, y: 84 }, { num: 16, x: 705, y: 84 },

  // === BOTTOM ROW ===
  // Admin standalone pair — 33 & 34
  { num: 33, x: 29,  y: 292 },
  { num: 34, x: 29,  y: 354 },
  // Pod B1 admin — desks 29–32
  { num: 31, x: 138, y: 292 }, { num: 29, x: 218, y: 292 },
  { num: 32, x: 138, y: 354 }, { num: 30, x: 218, y: 354 },
  // Pod B2 — desks 25–28
  { num: 27, x: 330, y: 292 }, { num: 25, x: 410, y: 292 },
  { num: 28, x: 330, y: 354 }, { num: 26, x: 410, y: 354 },
  // Pod B3 — desks 21–24
  { num: 23, x: 522, y: 292 }, { num: 21, x: 602, y: 292 },
  { num: 24, x: 522, y: 354 }, { num: 22, x: 602, y: 354 },
  // Pod B4 — desks 17–20
  { num: 20, x: 714, y: 292 }, { num: 17, x: 794, y: 292 },
  { num: 19, x: 714, y: 354 }, { num: 18, x: 794, y: 354 },
];

// Pod background rectangles — 7 px inner padding on each side
const POD_GROUPS = [
  // Top row — standalone pairs
  { x: 22,  y: 12,  w: 84,  h: 128, admin: false }, // T1 pair
  { x: 126, y: 12,  w: 84,  h: 128, admin: false }, // T2 pair
  // Top row — clusters of 4
  { x: 240, y: 12,  w: 164, h: 128, admin: false }, // T3
  { x: 429, y: 12,  w: 164, h: 128, admin: false }, // T4
  { x: 618, y: 12,  w: 164, h: 128, admin: false }, // T5
  // Bottom row — admin
  { x: 22,  y: 282, w: 84,  h: 130, admin: true  }, // B0 admin pair
  { x: 131, y: 282, w: 164, h: 130, admin: true  }, // B1 admin cluster
  // Bottom row — standard
  { x: 323, y: 282, w: 164, h: 130, admin: false }, // B2
  { x: 515, y: 282, w: 164, h: 130, admin: false }, // B3
  { x: 707, y: 282, w: 164, h: 130, admin: false }, // B4
];

function DeskFloorPlan({ deskData, selectedDeskNum, onDeskClick, onConfirm, onBack, bookingSummary, isRecurring, loading }) {
  const selected = selectedDeskNum ? deskData[selectedDeskNum] : null;
  const isAdminSelected = selectedDeskNum && ADMIN_DESKS.has(selectedDeskNum);

  const getDeskStyle = (num) => {
    const desk = deskData[num];
    if (!desk) return { fill: '#e0e0e0', stroke: '#bdbdbd', text: '#999', cursor: 'default' };
    if (num === selectedDeskNum) return { fill: '#B082E4', stroke: '#7B52AB', text: '#fff', cursor: 'pointer' };
    if (!desk.available) return { fill: '#ffcdd2', stroke: '#ef9a9a', text: '#b71c1c', cursor: 'not-allowed' };
    return { fill: '#c8e6c9', stroke: '#81c784', text: '#1b5e20', cursor: 'pointer' };
  };

  return (
    <Box>
      {/* Booking summary bar */}
      {bookingSummary && (
        <Paper variant="outlined" sx={{ p: 1.5, mb: 2, display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center', bgcolor: 'grey.50' }}>
          <Typography variant="body2"><strong>Date:</strong> {bookingSummary.date}</Typography>
          <Typography variant="body2"><strong>Time:</strong> {bookingSummary.start} – {bookingSummary.end}</Typography>
          {bookingSummary.name && <Typography variant="body2"><strong>Name:</strong> {bookingSummary.name}</Typography>}
          {bookingSummary.purpose && <Typography variant="body2"><strong>Purpose:</strong> {bookingSummary.purpose}</Typography>}
          <Button size="small" variant="text" onClick={onBack} sx={{ ml: 'auto' }}>← Back to details</Button>
        </Paper>
      )}

      {/* Floor plan SVG */}
      <Box sx={{ width: '100%', bgcolor: '#fafafa', border: '1px solid #e0e0e0', borderRadius: 2, overflow: 'hidden' }}>
        <svg viewBox="0 0 960 472" width="100%" style={{ display: 'block' }}>

          {/* ── Floor background ── */}
          <rect x="0" y="0" width="960" height="472" style={{ fill: '#f5f5f5' }} />

          {/* ── Walls — left wall has door gap 186–241 (midpoint between desk rows) ── */}
          <rect x="0"   y="0"   width="960" height="6"   style={{ fill: '#9e9e9e' }} /> {/* top         */}
          <rect x="0"   y="466" width="960" height="6"   style={{ fill: '#9e9e9e' }} /> {/* bottom      */}
          <rect x="0"   y="0"   width="6"   height="186" style={{ fill: '#9e9e9e' }} /> {/* left top    */}
          <rect x="0"   y="241" width="6"   height="231" style={{ fill: '#9e9e9e' }} /> {/* left bottom */}
          <rect x="954" y="0"   width="6"   height="472" style={{ fill: '#9e9e9e' }} /> {/* right       */}

          {/* ── Door symbol — hinge at (6,186), opens inward (rightward) ── */}
          {/* Door leaf: open position extends right from hinge */}
          <line x1="6" y1="186" x2="61" y2="186" stroke="#777" strokeWidth="2.5" />
          {/* Door swing arc: quarter-circle from (6,241) to (61,186), r=55 */}
          <path d="M 6 241 A 55 55 0 0 0 61 186" stroke="#777" fill="none" strokeWidth="1.5" strokeDasharray="3,2" />
          {/* Door-frame stops */}
          <rect x="0" y="184" width="10" height="4" style={{ fill: '#9e9e9e' }} />
          <rect x="0" y="239" width="10" height="4" style={{ fill: '#9e9e9e' }} />
          {/* Entrance arrow and label */}
          <text x="72" y="209" textAnchor="start" fontSize="8" style={{ fill: '#555' }} fontFamily="sans-serif" fontWeight="700">ENTRANCE</text>
          <text x="72" y="221" textAnchor="start" fontSize="7" style={{ fill: '#888' }} fontFamily="sans-serif">→</text>

          {/* ── Centre room label ── */}
          <text x="480" y="212" textAnchor="middle" fontSize="14" style={{ fill: '#d0d0d0' }} fontFamily="sans-serif" fontWeight="600" letterSpacing="2">
            COMMUNAL OFFICE · 34 DESKS
          </text>

          {/* ── Pod backgrounds ── */}
          {POD_GROUPS.map((pod, i) => (
            <rect
              key={i}
              x={pod.x} y={pod.y} width={pod.w} height={pod.h}
              style={{ fill: pod.admin ? '#fff8e1' : '#e8eaf6', stroke: pod.admin ? '#ffca28' : '#9fa8da' }}
              strokeWidth="1.5"
              rx="8"
              opacity="0.75"
            />
          ))}

          {/* ── Admin zone labels ── */}
          <text x="64"  y="278" textAnchor="middle" fontSize="9" style={{ fill: '#e65100' }} fontFamily="sans-serif" fontWeight="700">ADMIN</text>
          <text x="213" y="278" textAnchor="middle" fontSize="9" style={{ fill: '#e65100' }} fontFamily="sans-serif" fontWeight="700">ADMIN ONLY</text>

          {/* ── Desks ── */}
          {DESK_POSITIONS.map(({ num, x, y }) => {
            const s = getDeskStyle(num);
            const desk = deskData[num];
            const clickable = desk && desk.available;

            return (
              <g key={num} onClick={() => clickable && onDeskClick(num)} style={{ cursor: s.cursor }}>
                <rect
                  x={x} y={y} width={DESK_W} height={DESK_H}
                  style={{ fill: s.fill, stroke: s.stroke, strokeWidth: num === selectedDeskNum ? 2.5 : 1.5 }}
                  rx="5"
                />
                <text
                  x={x + DESK_W / 2} y={y + 19}
                  textAnchor="middle" fontSize="13" fontWeight="700"
                  style={{ fill: s.text }} fontFamily="sans-serif"
                >
                  {num}
                </text>
                <text
                  x={x + DESK_W / 2} y={y + 36}
                  textAnchor="middle" fontSize="8"
                  style={{ fill: s.text }} fontFamily="sans-serif" opacity="0.85"
                >
                  {desk ? (desk.available ? 'Available' : 'Booked') : '–'}
                </text>
              </g>
            );
          })}

          {/* ── Legend ── */}
          <rect x="10"  y="436" width="11" height="11" style={{ fill: '#c8e6c9', stroke: '#81c784' }} strokeWidth="1" rx="2" />
          <text x="25"  y="446" fontSize="9" style={{ fill: '#555' }} fontFamily="sans-serif">Available</text>

          <rect x="95"  y="436" width="11" height="11" style={{ fill: '#ffcdd2', stroke: '#ef9a9a' }} strokeWidth="1" rx="2" />
          <text x="110" y="446" fontSize="9" style={{ fill: '#555' }} fontFamily="sans-serif">Booked</text>

          <rect x="170" y="436" width="11" height="11" style={{ fill: '#B082E4', stroke: '#7B52AB' }} strokeWidth="1" rx="2" />
          <text x="185" y="446" fontSize="9" style={{ fill: '#555' }} fontFamily="sans-serif">Selected</text>

          <rect x="255" y="436" width="11" height="11" style={{ fill: '#fff8e1', stroke: '#ffca28' }} strokeWidth="1" rx="2" />
          <text x="270" y="446" fontSize="9" style={{ fill: '#555' }} fontFamily="sans-serif">Admin area</text>

        </svg>
      </Box>

      {/* ── Selection panel ── */}
      {selected && (
        <Paper variant="outlined" sx={{ mt: 2, p: 2.5, borderColor: 'primary.main', borderWidth: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, mb: 1.5 }}>
            <EventSeat sx={{ color: 'primary.main', mt: 0.25 }} />
            <Box>
              <Typography variant="h6" sx={{ lineHeight: 1.2 }}>{selected.room_name}</Typography>
              <Typography variant="body2" color="text.secondary">Desk {selectedDeskNum}</Typography>
            </Box>
          </Box>

          {isAdminSelected && (
            <Alert severity="warning" icon={<Warning />} sx={{ mb: 2 }}>
              <strong>Admin staff area.</strong> These desks are reserved for admin staff.
              This will be clearly noted on your booking confirmation.
            </Alert>
          )}

          {selected.equipment && selected.equipment.length > 0 ? (
            <Box sx={{ mb: 2 }}>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 0.75 }}>Equipment at this desk:</Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
                {selected.equipment.map((eq) => (
                  <Chip key={eq} label={eq} size="small" variant="outlined" />
                ))}
              </Box>
            </Box>
          ) : (
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>No equipment recorded for this desk.</Typography>
          )}

          <Divider sx={{ mb: 2 }} />

          <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
            <Button variant="outlined" onClick={() => onDeskClick(null)} disabled={loading}>
              Deselect
            </Button>
            <Button variant="contained" onClick={onConfirm} disabled={loading}>
              {loading ? 'Creating…' : isRecurring ? 'Preview Recurring Instances' : 'Confirm Booking'}
            </Button>
          </Box>
        </Paper>
      )}

      {!selected && (
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1.5, textAlign: 'center' }}>
          Click a green desk to select it
        </Typography>
      )}
    </Box>
  );
}

export default DeskFloorPlan;
